import { action } from '@ember/object';
import { later, scheduleOnce } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { PopupListItem } from 'webapp-types';

import FormInputSearchComponent from 'webapp/components/form/input-search';
import SearchableListItemComponent from 'webapp/components/searchable-list-item';

import type PopupList from 'webapp/models-custom/popup/popup-list';

type Item = PopupListItem<string>;

interface Args {
	itemsList: Item[];
	onAction: (action: Item['action'], id: Item['id']) => void;
	showSearchLimit?: number;
	showTextProperty?: keyof Item;
	searchableProperties?: (keyof Item)[];
	forceHideSearch?: boolean;
	theme?: string;
	popup?: PopupList;
}

export default class SearchableListComponent extends Component<Args> {
	@tracked typedFilter = '';

	get showTextProperty() {
		return this.args.showTextProperty ?? 'text';
	}

	get showSearchLimit() {
		// number of items from which the search line appears, as in the React dropdowns
		return this.args.showSearchLimit ?? 15;
	}

	get filterProperties() {
		return this.args.searchableProperties ?? ['text'];
	}

	get isSearchShown() {
		return !this.args.forceHideSearch && this.args.itemsList.length >= this.showSearchLimit;
	}

	get filteredItems() {
		return this.args.itemsList.filter(this.getFilter(this.filterProperties || []));
	}

	@action
	setupView(node: HTMLElement) {
		later(() => {
			const input = $(node).find('input');
			if (input.length) {
				input.focus();
			}
		}, 100); //in case of animation
	}

	@action
	triggerAction(item: Item) {
		/*
			item.action doesn't have to be set to call onAction (example redirectToLoginUrl in app\components\account\popup-spaces.ts which only uses item.id)
			so `if (!item.isDisabled && item.action) {` would break it
		*/
		if (!item.isDisabled) {
			this.args.onAction(item.action, item.id);
		}
	}

	@action
	changeTypedFilter(value: string) {
		this.typedFilter = value;
		scheduleOnce('afterRender', this, this.reposition);
	}

	private reposition() {
		$(window).trigger('resize.repositionpopup');
	}

	private getFilter(properties: (keyof Item)[]) {
		// replace multiple spaces with single space
		const typedFilter = (this.typedFilter || '').trim().replace(/\s+/g, ' ').toLowerCase();
		return (objItem: Item) => {
			return (
				!typedFilter ||
				properties.some((property) => {
					return String((objItem[property] as string) || '')
						.toLowerCase()
						.includes(typedFilter);
				})
			);
		};
	}
	<template>
		{{#if this.isSearchShown}}
			<div class="input-search-wrapper">
				<FormInputSearchComponent
					@value={{this.typedFilter}}
					@onChange={{this.changeTypedFilter}}
					@searchDelay={{500}}
					@placeholder="Search"
					@size="small"
					@autofocus={{true}}
					@theme={{@theme}}
				/>
			</div>
		{{/if}}
		<ul class="links context-ct {{if this.typedFilter 'search-active'}}">
			{{#each this.filteredItems as |item|}}
				<SearchableListItemComponent
					@item={{item}}
					@showTextProperty={{this.showTextProperty}}
					@onTriggerAction={{this.triggerAction}}
					@typedFilter={{this.typedFilter}}
					@popup={{@popup}}
				/>
			{{else}}
				<div class="no-items">No items found</div>
			{{/each}}
		</ul>
	</template>
}
